import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  FormGroup,
  Input,
  Row,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import AdminPage from './admin/AdminPage';
import Select from 'react-select';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useState, useCallback, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import useUserManager, { UserManagerServices } from '../hooks/useUserManager';
import { SuccessAlert } from '../models/alertModel';

interface Props {
  services: UserManagerServices;
  contextId?: string;
  pageName: string;
  pageParentName: string;
  getRolesPath: (userId: string | number) => string;
  showOption?: boolean;
  showPasswordOnReset?: boolean;
  renderExtraFields?: (control: any, errors: any) => React.ReactNode;
}

const UserGenericForm = ({
  contextId,
  services,
  pageName,
  pageParentName,
  getRolesPath,
  showOption = true,
  showPasswordOnReset = true,
  renderExtraFields,
}: Props) => {
  const {
    handleSubmit,
    doCreate,
    control,
    errors,
    isLoading,
    phone_config,
    areaCodes,
    resetField,
    sweetAlert,
    alertProps,
    searchQuery,
    setSearchQuery,
    filteredResults,
    generateNewPassword,
    confirmChangeStatus,
  } = useUserManager(services, contextId, showPasswordOnReset);

  const [phoneLength, setPhoneLength] = useState<number>(
    phone_config?.phone_length ?? 0,
  );
  const [copyStatus, setCopyStatus] = useState<'success' | 'error' | null>(
    null,
  );

  const handleCopyPassword = useCallback(async () => {
    const password = (alertProps as SuccessAlert)?.copyableText;

    if (!password) {
      setCopyStatus('error');
      return;
    }

    try {
      if (!navigator?.clipboard?.writeText) {
        throw new Error('Clipboard API not available');
      }

      await navigator.clipboard.writeText(password);
      setCopyStatus('success');
    } catch (error) {
      console.error(error);
      setCopyStatus('error');
    }
  }, [alertProps]);

  useEffect(() => {
    setCopyStatus(null);
  }, [alertProps]);

  const copyMessage =
    copyStatus === 'success'
      ? 'Contraseña copiada'
      : copyStatus === 'error'
      ? 'No se pudo copiar la contraseña'
      : '';
  const passwordValue = (alertProps as SuccessAlert)?.copyableText || '';
  const passwordLabel = passwordValue ? `Contraseña: ${passwordValue}` : '';
  const rawAlertText =
    typeof alertProps.text === 'string' ? alertProps.text : '';
  const cleanedAlertText = passwordValue
    ? rawAlertText.replace(passwordLabel, '').trim()
    : rawAlertText;

  return (
    <AdminPage name={pageName} parentName={pageParentName}>
      {/* Hace salto de linea en el pop-up */}
      {sweetAlert && (
        <SweetAlert {...alertProps}>
          {cleanedAlertText && (
            <div className="text-center" style={{ whiteSpace: 'pre-line' }}>
              {cleanedAlertText}
            </div>
          )}
          {passwordValue && (
            <div
              className="d-flex justify-content-center align-items-center flex-wrap mt-3"
              style={{ gap: 8 }}
            >
              <span className="mb-0 text-center">{passwordLabel}</span>
              <Button
                type="button"
                color="success"
                size="sm"
                onClick={handleCopyPassword}
              >
                Copiar
              </Button>
              {copyStatus && (
                <small
                  className={`ms-1 ${
                    copyStatus === 'success' ? 'text-success' : 'text-danger'
                  }`}
                >
                  {copyMessage}
                </small>
              )}
            </div>
          )}
        </SweetAlert>
      )}
      <Row>
        {/* Table Column */}
        <Col md="6">
          <h5>Resultados</h5>
          <Input
            type="text"
            placeholder="Buscar por nombre"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="mb-3"
          />
          <div
            style={{
              height: '500px',
              overflow: 'auto',
            }}
          >
            <Table className="align-items-center" responsive>
              <thead className="thead-light">
                <tr>
                  <th scope="col">Nombre</th>
                  <th scope="col">Correo electrónico</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.length > 0 ? (
                  filteredResults.map((result) => (
                    <tr key={result.id}>
                      <td>
                        {result.first_name} {result.last_name}
                      </td>
                      <td>{result.email}</td>
                      <td>
                        <Badge color={result.enabled ? 'success' : 'danger'}>
                          {result.enabled ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            className="btn-secondary"
                            href="#"
                            role="button"
                            size="sm"
                            color=""
                            caret
                            onClick={(e) => e.preventDefault()}
                          ></DropdownToggle>
                          <DropdownMenu
                            className="dropdown-menu-arrow"
                            right
                            container="body"
                          >
                            <DropdownItem
                              onClick={() => generateNewPassword(result.id)}
                            >
                              Generar nueva contraseña
                            </DropdownItem>
                            <DropdownItem
                              onClick={() =>
                                confirmChangeStatus(result.id, !result.enabled)
                              }
                            >
                              {result.enabled
                                ? 'Desactivar usuario'
                                : 'Activar usuario'}
                            </DropdownItem>
                            {showOption && (
                              <DropdownItem
                                tag={Link}
                                to={getRolesPath(result.id)}
                                state={{ userEmail: result.email }}
                              >
                                Asignar Roles
                              </DropdownItem>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No hay resultados que coincidan con la búsqueda
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Col>

        {/* Form Column */}
        <Col md="6">
          <Form role="form" onSubmit={handleSubmit(doCreate)}>
            {/* First Name */}
            <FormGroup>
              <label className="form-control-label" htmlFor="first_name">
                Nombres
              </label>
              <Controller
                name="first_name"
                control={control}
                defaultValue={''}
                render={({ field }) => (
                  <Input
                    className={errors.first_name?.message && 'is-invalid'}
                    {...field}
                    id="first_name"
                  />
                )}
              />
              <h5 className="text-danger">{errors.first_name?.message}</h5>
            </FormGroup>

            {/* Last Name */}
            <FormGroup>
              <label className="form-control-label" htmlFor="last_name">
                Apellidos
              </label>
              <Controller
                name="last_name"
                control={control}
                defaultValue={''}
                render={({ field }) => (
                  <Input
                    className={errors.last_name?.message && 'is-invalid'}
                    {...field}
                    id="last_name"
                  />
                )}
              />
              <h5 className="text-danger">{errors.last_name?.message}</h5>
            </FormGroup>

            {/* Email */}
            <FormGroup>
              <label className="form-control-label" htmlFor="email">
                Correo electrónico
              </label>
              <Controller
                name="email"
                control={control}
                defaultValue={''}
                render={({ field }) => (
                  <Input
                    className={errors.email?.message && 'is-invalid'}
                    {...field}
                    id="email"
                  />
                )}
              />
              <h5 className="text-danger">{errors.email?.message}</h5>
            </FormGroup>

            {/* Phone number */}
            <FormGroup>
              <label className="form-control-label" htmlFor="phone">
                Teléfono
              </label>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Controller
                  name="area_code"
                  control={control}
                  defaultValue={''}
                  render={({ field }) => {
                    const options = areaCodes.map((c) => ({
                      value: c.phone_area_code,
                      label: `${c.code} - ${c.phone_area_code}`,
                      phone_length: c.phone_length ?? 0,
                    }));
                    const selectedOption =
                      options.find((option) => option.value === field.value) ||
                      null;
                    return (
                      <div
                        style={{
                          minWidth: 120,
                          maxWidth: 200,
                          flex: '0 0 140px',
                        }}
                      >
                        <Select<
                          {
                            value: string;
                            label: string;
                            phone_length: number;
                          },
                          false
                        >
                          options={options}
                          value={selectedOption}
                          placeholder="Código"
                          isClearable
                          onChange={(option) => {
                            resetField('phone');
                            field.onChange(option ? option.value : '');
                            setPhoneLength(option?.phone_length ?? 0);
                          }}
                          styles={{
                            container: (base) => ({
                              ...base,
                              minWidth: 120,
                              maxWidth: 200,
                              flex: 1,
                            }),
                            control: (base) => ({
                              ...base,
                              minHeight: 38,
                              fontSize: 15,
                              paddingLeft: 2,
                              paddingRight: 2,
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: '0 6px',
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            singleValue: (base) => ({
                              ...base,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: 140,
                            }),
                          }}
                          onBlur={field.onBlur}
                          name={field.name}
                          ref={field.ref}
                        />
                      </div>
                    );
                  }}
                />
                <Controller
                  name="phone"
                  control={control}
                  defaultValue={''}
                  render={({ field: phoneField }) => (
                    <Input
                      className={errors.phone?.message && 'is-invalid'}
                      {...phoneField}
                      maxLength={phoneLength}
                      id="phone"
                      placeholder="Teléfono"
                      style={{ flex: 1, minWidth: 0 }}
                    />
                  )}
                />
              </div>
              <h5 className="text-danger">{errors.phone?.message}</h5>
            </FormGroup>
            {renderExtraFields && renderExtraFields(control, errors)}
            <Button color={'primary'} disabled={isLoading}>
              Crear
            </Button>
          </Form>
        </Col>
      </Row>
    </AdminPage>
  );
};

export default UserGenericForm;
